/**
 * Everything behind the public site needs a session.
 *
 * The `app.` host **and every festival subdomain**. The only hosts that render
 * without one are the bare platform domain (the marketing site) and
 * `login.` itself.
 *
 * ---
 *
 * **This reverses a documented decision, at the user's instruction
 * (2026-09-24: "app.* cannot be access if user is not logged in … its include
 * all subdomains").**
 *
 * `frontend/CLAUDE.md` and `plan.md` §9.1 had it the other way round: *"A
 * festival's own subdomain is unaffected, and so is an artist page reached by
 * a direct link. Those are places someone was SENT; the directory is a way to
 * BROWSE, and only browsing needs an account."* Under that rule somebody
 * handed a festival's own address on a flyer saw the lineup immediately.
 *
 * They now see a login page. That is a real change in what the product is, not
 * a routing detail — so the homepage line promising "no account needed" has
 * been removed with it rather than left standing as something untrue.
 *
 * **The guard is on the HOST, not on a screen.** Putting it on `Directory`
 * would leave `/artists/:slug` and every route added later open, which is the
 * §4.0 shape: a decision computed correctly in one place and not enforced in
 * the others.
 *
 * **Offline, it never sends anybody to the login host.** `login.` is another
 * origin and the service worker cannot serve it: with no signal it is a dead
 * page. The boot refresh gives up after 2.5s and, when it could not hear the
 * server, `AuthProvider` reports `offline` with the account last signed in on
 * this origin — so the rave opens from what is stored. Somebody never signed
 * in here, offline, is told that signing in needs a signal, and stays put.
 * Only a real answer ("nobody is signed in") redirects.
 *
 * **A failed check does NOT lock somebody out.** `AuthProvider` reports
 * `loading` until the boot refresh settles and only then a null user. A
 * network failure that leaves the check unresolved must never be what stands
 * between somebody and the schedule they saved — the same rule
 * `CompleteAccount` follows.
 */
import { useEffect, type ReactNode } from 'react';
import { ErrorState, LoadingScreen } from '@/ui';
import { backdrop } from './backdrop';
import { useTenant } from './TenantProvider';
import { loginUrl, platformDomain } from '@/lib/tenant';
import { rememberReturn } from './ReturnAfterSignIn';
import { bouncedRecently, clearBounce, markBounce } from './loginBounce';
import { useAuth } from './AuthProvider';

export { BOUNCE_WINDOW_MS } from './loginBounce';
import { useT } from '@/i18n';

/**
 * Did we arrive here FROM the login page?
 *
 * If so, signing in did not produce a session this host can see, and
 * redirecting back would bounce the browser between two pages forever. That is
 * not hypothetical: the refresh cookie is scoped to the API host, so on
 * `localhost` — where `app.localhost → localhost:3000` is cross-site and
 * `SameSite=Lax` withholds it — the session genuinely does not survive the
 * hop. In production both are subdomains of the platform domain and it does.
 *
 * So the loop is broken and the reason is shown, rather than the tab spinning.
 *
 * **A referrer is not enough on its own.** It is empty whenever one is stripped
 * — a policy, a privacy setting, a page opened directly — and then this returns
 * false and the brake is OFF in exactly the case it exists for (measured on dev,
 * 2026-10-28: `document.referrer` was `''` while the login page reloaded in a
 * cycle). `bouncedRecently()` below is the hop this TAB recorded on its way
 * out, and the two together are the brake.
 */
function cameFromLogin(): boolean {
  if (!document.referrer) return false;
  try {
    return new URL(document.referrer).hostname.toLowerCase()
      === `login.${platformDomain().toLowerCase()}`;
  } catch {
    return false;
  }
}

export function RequireSession({ children, signedOut, open }: {
  children: ReactNode;
  /**
   * What a rave's host shows somebody with no session INSTEAD of the
   * redirect to the sign-in (the user, 2026-10-14: the rave's landing page).
   * Only on a definite "nobody is signed in": offline, the remembered account
   * still opens the app, and a bounce from the login page still explains itself.
   */
  signedOut?: ReactNode;
  /**
   * Open to somebody signed out (the user, 2026-10-26: "the app will not require a login at
   * the opening … users will be able to access the raves and see the scheduling and the
   * map"): the children render with no session, and whatever is somebody's own asks for
   * the sign-in itself (`lib/signInFirst.ts`). The API decides what a stranger may read.
   */
  open?: boolean;
}) {
  const tr = useT();
  const { user, loading, offline, refresh } = useAuth();
  const { tenant } = useTenant();
  const bounced = cameFromLogin() || bouncedRecently();

  useEffect(() => { if (user) clearBounce(); }, [user]);

  useEffect(() => {
    if (loading || user || bounced || offline || signedOut || open) return;
    // A full navigation: `login.` is another host, which the router cannot
    // reach. `replace` keeps this page out of the back stack, so Back from
    // the login page does not land on a gate that redirects again.
    // `/plans` is remembered first, so the landing comes back to it
    // (`ReturnAfterSignIn.tsx` — a short list, never a caller's destination).
    rememberReturn();
    // Recorded BEFORE the hop, because the brake must not depend on a referrer
    // surviving it. Cleared once a session reaches this host, and by sign-out.
    markBounce();
    window.location.replace(loginUrl());
  }, [loading, user, bounced, offline, signedOut, open]);

  // On a rave, its cover behind the wait — the same backdrop the lineup's
  // waiting screen wears (`EditionProvider`), so the artwork arrives with the
  // tenant instead of after the session check (step 14, 2026-10-05).
  // An open page draws at once (the user, 2026-10-27: "loading screen, then the home page,
  // then again loading screen and again the home page") — it waited here for the session, then
  // drew the header and started its own load: two loading screens with the page between.
  // It needs no account, so it loads alongside the session check; what is somebody's own
  // still waits, route by route (a nested `RequireSession`).
  if (loading && open) return <>{children}</>;
  if (loading) {
    const wait = <LoadingScreen label={tr('One moment…')} />;
    return tenant?.coverUrl
      ? <div className="edition-backdrop" style={backdrop(tenant.coverUrl)}>{wait}</div>
      : wait;
  }

  if (!user && open) return <>{children}</>;

  if (!user && offline) {
    return (
      <ErrorState
        title={tr('Signing in needs a signal')}
        detail={tr('This phone is offline and nobody has signed in here yet. Once there is a connection, this page picks up on its own.')}
        retry={() => void refresh()}
        retryLabel={tr('Try again')}
      />
    );
  }

  if (!user) {
    if (!bounced && signedOut) return <>{signedOut}</>;
    if (!bounced) return <LoadingScreen label={tr('Taking you to sign in…')} />;
    return (
      <ErrorState
        /*
         * **"You are on the list" is the APPROVAL screen's sentence, and this
         * is not about approval.**
         *
         * `AwaitingApproval` opens with exactly those words. Two unrelated
         * screens said the same thing, so a signed-in platform admin landing
         * here read it as "my account has not been approved" and went looking
         * for an approval that had never been missing — twice, on 2026-09-27,
         * before anybody noticed the wording was the whole problem.
         *
         * What this screen actually means is narrower and duller: the sign-in
         * worked and the session did not survive the hop between two hosts. It
         * says that now, and offers the only thing that helps.
         */
        title={tr('Your sign-in did not reach this page')}
        detail={tr('It worked — the session just did not carry across to this address. Sign in again here. If it keeps happening, clear this site’s cookies and try once more.')}
        retry={() => { rememberReturn(); markBounce(); window.location.replace(loginUrl()); }}
        retryLabel={tr('Sign in again')}
      />
    );
  }

  return <>{children}</>;
}
